import type { ReadonlyMat3 } from 'gl-matrix';
import { imageRowPatientAxis } from './geometry';
import { isPatientPositionInconsistentWithGrid } from './patientPose';

/** Patient Z — superior-inferior. Never a couch-depth direction. */
const PATIENT_LONGITUDINAL_AXIS = 2;

/**
 * Why a series cannot receive a couch model, or nothing when it can.
 * Domain facts only: each caller maps the reason to its own wording, so
 * the toolbar tooltip, the detect error and any future surface refuse
 * the same series for the same reason.
 */
export type CouchPlacementBlocker =
    | 'no-image'
    | 'not-hu-quantified'
    | 'grid-not-axis-aligned'
    | 'grid-not-transverse'
    | 'patient-position-mismatch';

/**
 * The slice of a series the couch tool's eligibility depends on. A
 * structural type rather than `Image3D` so the predicate stays testable
 * with plain objects and so the domain never reaches past these three
 * facts.
 */
export interface CouchPlacementImage {
    readonly isHuQuantified: boolean;
    readonly firstSlice: { readonly patientPosition: string | undefined };
    readonly grid: {
        isAxisAligned(): boolean;
        getOrientation(): ReadonlyMat3;
    };
}

/**
 * The couch tool accepts an HU-quantified CT on a purely axis-aligned
 * grid whose recorded pose agrees with that grid. Axis-aligned includes
 * the decubitus permutations and the four axial setups; it excludes a
 * genuinely oblique acquisition, whose slice planes cut a different shape
 * through a rigid couch than its axial cross-section, so extruding an
 * axial profile onto them is wrong however placement is written.
 *
 * Axis-alignment alone is not enough. A coronal or sagittal volume is
 * perfectly axis-aligned, but its image column direction runs along the
 * patient's superior-inferior axis, which is never where the couch is,
 * so the whole depth premise is void: placement would extrude the couch
 * along the patient's length. Neither the obliquity test nor the pose
 * test catches it — the first because the grid IS axis-aligned, the
 * second because such a grid implies no standard patient position at all
 * and so can never be caught disagreeing with one.
 *
 * The GRID is what decides obliquity, not the DICOM per-slice `tilted`
 * flag: `placeCouchModel` refuses on `Grid.isAxisAligned()` and a gate
 * that consulted anything else could pass a series placement then throws
 * on. The flag classifies the same six direction cosines at parse time
 * and adds nothing here.
 *
 * Reasons are returned in the order the user would fix them, and that
 * order is also a safety property: a series that is not HU-quantified is
 * refused BEFORE its grid is read, so a PET or a partially loaded image
 * with no usable grid reports the real reason instead of throwing.
 * The pose comparison is last because its premise (see
 * `isPatientPositionInconsistentWithGrid`) is only stated over
 * axis-aligned grids.
 */
export function couchPlacementBlocker(
    image: CouchPlacementImage | undefined
): CouchPlacementBlocker | undefined {
    if (!image) {
        return 'no-image';
    }
    if (!image.isHuQuantified) {
        return 'not-hu-quantified';
    }
    if (!image.grid.isAxisAligned()) {
        return 'grid-not-axis-aligned';
    }
    const orientation = image.grid.getOrientation();
    if (imageRowPatientAxis(orientation) === PATIENT_LONGITUDINAL_AXIS) {
        return 'grid-not-transverse';
    }
    if (
        isPatientPositionInconsistentWithGrid(
            image.firstSlice.patientPosition,
            orientation
        )
    ) {
        return 'patient-position-mismatch';
    }
    return undefined;
}
